import { toNullableNumber, toNullableString } from "@/utility/common-utils";
import { DASHBOARD_CARD_CONFIG } from "./dashboard-profile-card.config";
import type { DashboardCardConfig, DashboardProfile, DashboardResolvedStatus } from "./types";

interface DashboardCategoryApiResponse {
  category_id: string | number;
  access_id?: string | number | null;
  access_name?: string | null;
  access_display_code?: string | null;
  category_name?: string | null;
  key?: string | null;
  category_display_code?: string | null;
  plan_id?: string | number | null;
  user_plan_id?: string | number | null;
  user_id?: string | number | null;
  plan_group?: string | null;
  plan_days_remaining?: string | number | null;
  plan_listing_remaining?: string | number | null;
  rating?: string | number | null;
  status?: string | null;
  userPlanStatus?: string | null;
  leads?: string | number | null;
  appointments?: string | number | null;
  postings?: string | number | null;
  referrals?: string | number | null;
  earnings?: string | number | null;
}

export const createDashboardProfile = (data: DashboardCategoryApiResponse): DashboardProfile => ({
  categoryId: String(data.category_id),
  userAccessId: toNullableString(data.access_id),
  accessName: data.access_name ?? "",
  categoryName: data.category_name ?? "",
  categoryKey: data.key ?? "",
  displayCode: data.category_display_code ?? data.access_display_code ?? "",
  planId: toNullableString(data.plan_id),
  userPlanId: toNullableString(data.user_plan_id),
  userId: toNullableString(data.user_id),
  planName: toNullableString(data.plan_group),
  planDaysRemaining: toNullableNumber(data.plan_days_remaining),
  postsRemaining: toNullableNumber(data.plan_listing_remaining),
  rating: toNullableNumber(data.rating),
  status: toNullableString(data.status ?? data.userPlanStatus),
  metrics: {
    leads: toNullableNumber(data.leads),
    appointments: toNullableNumber(data.appointments),
    postings: toNullableNumber(data.postings),
    referrals: toNullableNumber(data.referrals),
    earnings: toNullableNumber(data.earnings),
  },
});

export const resolveDashboardCardConfig = (categoryId: string): DashboardCardConfig => {
  const base = DASHBOARD_CARD_CONFIG.default;
  const category = DASHBOARD_CARD_CONFIG.categories[categoryId];
  if (!category) return base;
  return {
    ...base,
    ...category,
    createPost: { ...base.createPost, ...category.createPost },
    knowMore: {...base.knowMore, ...category.knowMore},
    kpis: category.kpis ?? base.kpis,
    extraKpis: category.extraKpis ?? base.extraKpis
  };
};

export const isDashboardPlanExpired = (
  profile: DashboardProfile,
  config: DashboardCardConfig,
) => config.renewOnExpiry
  && profile.planDaysRemaining !== null
  && profile.planDaysRemaining <= 0;

export const resolveDashboardStatus = (
  profile: DashboardProfile,
  config: DashboardCardConfig,
  agreementSigned: boolean | null,
): DashboardResolvedStatus | null => {
  if (isDashboardPlanExpired(profile, config)) {
    return { labelKey: "label.expired", tone: "danger", action: config.renewAction };
  }
  if (profile.status === "PENDING_APPROVAL") {
    return { labelKey: "label.pending_for_approval", tone: "warning" };
  }
  if (config.requiresAgreement && agreementSigned === false) {
    return { labelKey: "label.sign_agreement", tone: "warning", action: { type: "dialog", name: "SIGN_AGREEMENT" } };
  }
  if (profile.status === "APPROVED") {
    return { labelKey: "label.approved", tone: "success" };
  }
  return profile.status ? { labelKey: profile.status, tone: "muted" } : null;
};

export const hasKnowMoreCard = (config: DashboardCardConfig) : boolean => {
  return Boolean(config.visibility === 'knowMore');
}

export const hasCreatePostCard = (config: DashboardCardConfig) : boolean => {
  return Boolean(config.visibility === 'createPost')
}
